import React, { useState } from "react"
import {
    TableContainer,
    TableHead,
    TableCell,
    Table,
    TableBody,
    TableRow,
    FormControlLabel,
    Checkbox,
    Stack,
    Typography
} from "@mui/material"
import { withStyles } from "@mui/styles"
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Timer as TimerIcon,
    OfflineBolt as OfflineBoltIcon
} from "@mui/icons-material"
import Button from "@mui/material/Button"
import { Formik, Form, Field, ErrorMessage } from "formik"
import cloneDeep from "lodash/cloneDeep"
import _get from "lodash/get"
import uniqBy from "lodash/uniqBy"
import { RecipeDetailsStyles } from "../../styles"
import { getDefaultHeatingInstruction, getRecipePackagingNumberUnitLabel, recipeDetailsFormInitialValues } from "../../actions/Utils/utils"
import { getBrandBy, forEachBrand, COLORS } from "../../utils"
import FormikTextArea from "./FormikTextArea"
import {
    getPackagings,
    getSubPackagings,
    getReusablePackagings,
    getReusableSubPackagings
} from "../../reducers/Subcontractor/subcontractorsProducts"
import MultiSelectAutoCompleteNew from "../autoComplete/MultiSelectAutoCompleteNew"
import { emptySchema, getRecipeDetailsSchema } from "../../utils/yupValidators"
import { openUploadWidget } from "../../utils/cloudinary"
import {
    recipePreparations,
    difficultiesChooser,
    recipePreparationsBasic
} from "../../utils/recipes"
import CloudinaryImage from "../Image/CloudinaryImage"

const FormikCheckbox = ({
    field,
    form,
    ...props
}) => {
    const { name, value } = field
    const { transformValue, parseValue } = props

    const handleChange = (e) => {
        // ex: true => on, false => off
        const inputValue = transformValue ? transformValue(e.target.checked) : e.target.checked
        form.setFieldValue(field.name, inputValue)
    }

    return (
        <FormControlLabel
            {...props}
            {...field}
            control={<Checkbox />}
            name={name}
            // ex: on => true, off => false
            checked={parseValue ? parseValue(value) : value}
            onChange={handleChange}
            sx={{
                "& .MuiFormControlLabel-label": {
                    fontSize: 14,
                    color: COLORS.BLACK800
                }
            }}
        />
    )
}

const PackagingNumberPreview = ({ value, unit }) => {
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pl: 1.1 }}>
            <Typography variant="body2" sx={{ color: COLORS.LABEL_GREY }}>
                {value}
            </Typography>
            {unit && (
                <Typography variant="body2" sx={{ color: COLORS.BLACK_500 }}>
                    ({getRecipePackagingNumberUnitLabel(unit)})
                </Typography>
            )}
        </Stack>  
    )
}

const ENABLED_CONFIG = { price: "priceEnabled" }
// Fields that carry an Enterprise-scoped entry (KFC-294) — same list as server's ENTERPRISE_SCOPED_RECIPE_FIELDS, minus
// foodCostPCT (not shown in this grid) and packagingNumber/packagingNumberUnit (rendered by their own dedicated row, see _renderPackagingNumberRow)
const ENTERPRISE_SCOPED_FIELDS = new Set([
    "description", "price", "instructions", "dlc", "heatingInstructions",
    "packaging", "subPackaging", "reusablePackaging", "reusableSubPackaging",
    "appImage", "kitchenImage", "commercialNames", "tva"
])
const FIELD_TYPES = {
    TEXT: "text",
    TEXTAREA: "textarea",
    SELECT: "select",
    MULTISELECTAUTOCOMPLETE: "multiselectautocomplete",
    ICON: "icon",
    NUMBER: "number",
    CHECKBOX: "checkbox"
}
const HEATING_FIELDS = [
    { key: "power", fieldType: FIELD_TYPES.ICON, icon: <OfflineBoltIcon /> },
    { key: "duration", fieldType: FIELD_TYPES.ICON, icon: <TimerIcon /> },
    { key: "instructions", fieldType: FIELD_TYPES.TEXTAREA, icon: null, props: { placeholder: "Instructions" } }
]
const PREPARATION_VALUES = [
    { value: "1", label: "Se mange froid" },
    { value: "2", label: "Se mange chaud" },
    { value: "3", label: "Se mange chaud ou froid" }
]

const Details = (props) => {
    const {
        classes,
        editing,
        onEdit,
        bindSubmitAndResetForm,
        parseRecipe,
        onStopEdit,
        updateRecipeDetails,
        packagings,
        productType,
        enterprises = []
    } = props

    const [schema, setSchema] = useState(parseRecipe ? getRecipeDetailsSchema(parseRecipe.get("status"), parseRecipe.get("preparation")) : emptySchema)

    const _buildPackagings = (packagings, subPackagings = false, allowNull = false, reusable = false) => {
        const result = {}

        forEachBrand(brand => {
            let filteredpackagings = []

            if (reusable) {
                filteredpackagings = subPackagings
                    ? getReusableSubPackagings(packagings, brand.name)
                    : getReusablePackagings(packagings, brand.name)
            }
            else {
                filteredpackagings = subPackagings
                    ? getSubPackagings(packagings, brand.name)
                    : getPackagings(packagings, brand.name)
            }

            result[brand.name] = filteredpackagings && Array.isArray(filteredpackagings)
                ? filteredpackagings.map(p => ({ value: p.id, label: p.get("name") }))
                : []

            if (allowNull) {
                result[brand.name].unshift({ value: "", label: "Aucun" })
            }
        })

        // Enterprise columns: filter by enterprise association (backward compat: no enterprises = visible partout)
        const allOfType = reusable
            ? subPackagings ? getReusableSubPackagings(packagings) : getReusablePackagings(packagings)
            : subPackagings ? getSubPackagings(packagings) : getPackagings(packagings)

        for (const enterprise of (enterprises || [])) {
            const eid = enterprise.objectId
            const filtered = allOfType.filter(p => {
                const linked = p.toJSON().enterprises || []
                return linked.some(e => e.objectId === eid)
            })
            result[eid] = filtered.map(p => ({ value: p.id, label: p.get("name") }))
            if (allowNull) {
                result[eid].unshift({ value: "", label: "Aucun" })
            }
        }

        return result
    }

    // One "column" per brand, then one per Enterprise attached to the recipe (KFC-294)
    const _getColumns = (values) => [
        ...values.brands.map((brand) => ({ key: brand, isEnterprise: false })),
        ...(values.enterprises || []).map((enterpriseId) => ({ key: enterpriseId, isEnterprise: true }))
    ]

    const _tvaChooser = () => {
        return [5.5, 10, 20].map(el => ({ value: el, label: el }))
    }

    const _buildDifficultyChooser = () => {
        return difficultiesChooser.map(el => ({ value: el, label: el }))
    }

    const _onSubmit = (values) => {
        updateRecipeDetails(cloneDeep(values), parseRecipe, false).then(saved => saved && onStopEdit())
    }

    const _updatePreparation = (values, value, recipeType, setFieldValue) => {
        const type = productType.find(item => item.value === recipeType)
        let preparation
        if (type.value === "DRINK") {
            preparation = recipePreparationsBasic.find(item => item.value === value)
        }
        else {
            preparation = recipePreparations.find(item => item.value === value)
        }

        setFieldValue("preparation", value, false) // No validation here, because there's a second setFieldValue after
        setFieldValue(
            "specialInstruction",
            `${type.adj} ${type.label.toLowerCase()} ${type.adj === "Ce" ? preparation.label.toLowerCase() : preparation.labelFem.toLowerCase()}`,
            false
        ) // No validation here, because there's a second setFieldValue after

        if (value !== "1") {
            const finalHeatingInstructions = []
            let updated = false

            for (const brand of values.brands) {
                const currentHeatingInstruction = values.heatingInstructions.find(el => el.brand === brand)

                if (!currentHeatingInstruction) {
                    updated = true
                    finalHeatingInstructions.push({
                        brand: brand,
                        value: getDefaultHeatingInstruction(brand)
                    })
                }
            }

            if (updated) {
                setFieldValue("heatingInstructions", finalHeatingInstructions)
            }
        }
        else {
            setFieldValue("heatingInstructions", [])
        }

        setSchema(getRecipeDetailsSchema(parseRecipe.get("status"), value))
    }

    // column is null for the recipe-level default image, or a { key, isEnterprise } column otherwise
    const _matchesColumn = (el, column) => column.isEnterprise ? el.enterprise === column.key : el.brand === column.key

    const _uploadAppImage = (values, setFieldValue, column) => {
        openUploadWidget({ tags: ["app"] }, (error, result) => {
            if (error) {
                console.error(error)
                return
            }

            if (result.event !== "success") {
                // we don't use this event
                return
            }

            const publicId = result.info.public_id

            //---------- Where do we put the publicId ? ---------//
            if (!column) {
                const defaultValues = values.defaultValues
                defaultValues.appImage = { publicId }
                setFieldValue("defaultValues", defaultValues)
            }
            else {
                const images = [...(values.appImage ? values.appImage : [])]
                const columnImage = images.find(img => _matchesColumn(img, column))

                if (columnImage) {
                    columnImage.value = { publicId }
                    setFieldValue("appImage", images)
                }
                else {
                    images.push({
                        ...(column.isEnterprise ? { enterprise: column.key } : { brand: column.key }),
                        value: { publicId }
                    })
                    setFieldValue("appImage", images)
                }
            }
        })
    }

    const _uploadKitchenImage = (values, setFieldValue, column) => {
        openUploadWidget({ tags: ["kitchen"] }, (error, result) => {
            if (error) {
                console.error(error)
                return
            }
            if (result.event !== "success") {
                // we don't use this event
                return
            }

            const publicId = result.info.public_id
            const images = values.kitchenImage || []
            const columnImage = images && images.find(img => _matchesColumn(img, column)) || null

            if (columnImage) {
                columnImage.value = { publicId }
                setFieldValue("kitchenImage", images)
            }
            else {
                images.push({
                    ...(column.isEnterprise ? { enterprise: column.key } : { brand: column.key }),
                    value: { publicId }
                })
                setFieldValue("kitchenImage", images)
            }
        })
    }

    const _deleteAppImage = (values, column, setFieldValue) => {
        if (!column) {
            const defaultValues = values.defaultValues
            defaultValues.appImage = null
            setFieldValue("defaultValues", defaultValues)
        }
        else {
            const images = values.appImage
            images.splice(images.findIndex(el => _matchesColumn(el, column)), 1)
            setFieldValue("appImage", images)
        }
    }

    const _deleteKitchenImage = (values, column, setFieldValue) => {
        setFieldValue("kitchenImage", (values.kitchenImage || []).filter(el => !_matchesColumn(el, column)))
    }

    const _renderField = (fieldType, { options, brand, icon, secondType, ...props }) => {
        let result = null
        // options can be a flat array (shared across columns) or a map keyed by KFC brand name
        // (packaging lists). An Enterprise column has no key in that map — fall back to the
        // union of every brand's options rather than crashing on a non-array .map() call.
        const resolvedOptions = options && !Array.isArray(options)
            ? options[brand] || uniqBy(Object.values(options).flat(), "value")
            : options

        switch (fieldType) {
            case FIELD_TYPES.TEXT:
            case FIELD_TYPES.NUMBER:
                result = <Field type={fieldType} {...props} />
                break
            case FIELD_TYPES.TEXTAREA:
                result = <Field component={FormikTextArea} {...props} />
                break
            case FIELD_TYPES.CHECKBOX:
                result = <Field component={FormikCheckbox} {...props} />
                break
            case FIELD_TYPES.SELECT:
                result = (
                    <Field component="select" {...props}>
                        {
                            props.placeholder &&
                            <option value="" disabled="disabled" selected="disabled">{props.placeholder}</option>
                        }
                        {
                            resolvedOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {option.label}
                                </option>
                            ))
                        }
                    </Field>
                )
                break
            case FIELD_TYPES.MULTISELECTAUTOCOMPLETE:
                result = <Field component={MultiSelectAutoCompleteNew} options={resolvedOptions} {...props} />
                break
            case FIELD_TYPES.ICON:
                result = (
                    <div className={classes.iconField}>
                        {icon}
                        {_renderField(secondType, props)}
                    </div>
                )
                break
            default:
            // do nothing
        }

        return result
    }

    const _renderGenericRow = (values, fieldName, fieldType, label, errors, enableDefault = true, props = {}) => {
        const field = values[fieldName]

        const isBrandField = field !== null && field !== undefined && Array.isArray(field)
        const defaultFieldName = `${isBrandField ? "defaultValues." : ""}${fieldName === "commercialNames" ? "commercialName" : fieldName}`
        const { placeholder } = props

        return (
            <TableRow>
                <TableCell className={classes.leftHeader}>{label}</TableCell>
                <TableCell>
                    {
                        enableDefault && (
                            <>
                                {_renderField(fieldType, {
                                    name: defaultFieldName,
                                    disabled: !editing,
                                    className: _get(errors, defaultFieldName, false) ? classes.fieldError : null,
                                    ...props
                                })}
                                <ErrorMessage
                                    name={defaultFieldName}
                                    render={msg => <div className={classes.error}>{msg}</div>}
                                />
                            </>
                        )
                    }
                </TableCell>
                {
                    isBrandField && _getColumns(values).map((column, index) => {
                        if (column.isEnterprise && !ENTERPRISE_SCOPED_FIELDS.has(fieldName)) {
                            return (
                                <TableCell key={index}>
                                    <span className={classes.default}>Non applicable</span>
                                </TableCell>
                            )
                        }

                        let enabled = true

                        if (ENABLED_CONFIG[fieldName]) {
                            if (!column.isEnterprise) {
                                enabled = getBrandBy("name", column.key)[ENABLED_CONFIG[fieldName]]
                            }
                            else {
                                // An Enterprise has no priceEnabled-like flag of its own: only treat the field as
                                // applicable when a real value was actually copied from its source (never guess via defaultValues)
                                const enterpriseEntry = field.find(el => _matchesColumn(el, column))
                                enabled = !!enterpriseEntry && enterpriseEntry.value !== null && enterpriseEntry.value !== undefined
                            }
                        }

                        const entryIndex = field.findIndex(el => _matchesColumn(el, column))

                        return (
                            <TableCell key={index}>
                                {!enabled && <span className={classes.default}>Non applicable</span>}
                                {enabled &&
                                    <>
                                        {_renderField(fieldType, {
                                            name: `${fieldName}[${entryIndex}].value`,
                                            disabled: !editing,
                                            brand: column.key,
                                            placeholder: undefined === placeholder ? "Par défaut" : placeholder,
                                            className: _get(errors, `${fieldName}[${entryIndex}].value`, false) ? classes.fieldError : null,
                                            ...props
                                        })}
                                        <ErrorMessage
                                            name={`${fieldName}[${entryIndex}].value`}
                                            render={msg => <div className={classes.error}>{msg}</div>}
                                        />
                                    </>
                                }
                            </TableCell>
                        )
                    })
                }
                {
                    !isBrandField && _getColumns(values).map((_, index) =>
                        <TableCell key={index}>
                            <span className={classes.default}>Non applicable</span>
                        </TableCell>
                    )
                }
            </TableRow>
        )
    }

    const _renderPackagingNumberRow = (values, errors, enableDefault = true, props = { }) => {
        const fieldName = "packagingNumber"
        const checkboxFieldName = "packagingNumberUnit"
        const label = "Nombre par barquette"
        const field = values[fieldName]
        const checkboxField = values[checkboxFieldName]
        const checkboxLabel = "En entier"

        const isBrandField = field !== null && field !== undefined && Array.isArray(field)
        const defaultFieldName = `${isBrandField ? "defaultValues." : ""}${fieldName}`
        const checkboxDefaultFieldName = `${isBrandField ? "defaultValues." : ""}${checkboxFieldName}`
        
        const { placeholder } = props
        return (
            <TableRow>
                <TableCell className={classes.leftHeader}>{label}</TableCell>
                <TableCell>
                    {editing
                        ? (
                            enableDefault && (
                                <Stack>
                                    <Stack>
                                        <Stack direction="row" alignItems="center" spacing={0.7}>
                                            {_renderField(FIELD_TYPES.NUMBER, {
                                                name: defaultFieldName,
                                                disabled: !editing,
                                                className: _get(errors, defaultFieldName, false) ? classes.fieldError : null,
                                                ...props
                                            })}
                                            <Typography variant="body2" sx={{ color: COLORS.LABEL_GREY }}>
                                                portions
                                            </Typography>
                                        </Stack>
                                        <ErrorMessage
                                            name={defaultFieldName}
                                            render={msg => <div className={classes.error}>{msg}</div>}
                                        />
                                    </Stack>
                                    <Stack sx={{ pl: 1.3 }}>
                                        {_renderField(FIELD_TYPES.CHECKBOX, {
                                            name: checkboxDefaultFieldName,
                                            disabled: !editing,
                                            label: checkboxLabel,
                                            className: _get(errors, checkboxDefaultFieldName, false) ? classes.fieldError : null,
                                            transformValue: (value) => value ? "full" : "piece",
                                            parseValue: (value) => value === "full",
                                            ...props
                                        })}
                                        <ErrorMessage
                                            name={checkboxDefaultFieldName}
                                            render={msg => <div className={classes.error}>{msg}</div>}
                                        />
                                    </Stack>
                                </Stack>
                            )
                        ) : (
                            <PackagingNumberPreview
                                value={values.defaultValues.packagingNumber}
                                unit={values.defaultValues.packagingNumberUnit}
                            />
                        )
                    }
                </TableCell>
                {
                    isBrandField && _getColumns(values).map((column, index) => {
                        const entryIndex = field.findIndex(el => _matchesColumn(el, column))
                        let enabled = true

                        return (
                            <TableCell key={index}>
                                {!enabled && <span className={classes.default}>Non applicable</span>}
                                {editing
                                    ? (
                                        enabled &&
                                        <Stack>
                                            <Stack>
                                                <Stack direction="row" alignItems="center" spacing={0.7}>
                                                    {_renderField(FIELD_TYPES.NUMBER, {
                                                        name: `${fieldName}[${entryIndex}].value`,
                                                        disabled: !editing,
                                                        brand: column.key,
                                                        placeholder: undefined === placeholder ? "Par défaut" : placeholder,
                                                        className: _get(errors, `${fieldName}[${entryIndex}].value`, false) ? classes.fieldError : null,
                                                        ...props
                                                    })}
                                                    <Typography variant="body2" sx={{ color: COLORS.LABEL_GREY }}>
                                                        portions
                                                    </Typography>
                                                </Stack>

                                                <ErrorMessage
                                                    name={`${fieldName}[${entryIndex}].value`}
                                                    render={msg => <div className={classes.error}>{msg}</div>}
                                                />
                                            </Stack>
                                            <Stack sx={{ pl: 1.3 }}>
                                                {_renderField(FIELD_TYPES.CHECKBOX, {
                                                    name: `${checkboxFieldName}[${entryIndex}].value`,
                                                    disabled: !editing,
                                                    label: checkboxLabel,
                                                    brand: column.key,
                                                    transformValue: (value) => value ? "full" : "piece",
                                                    parseValue: (value) => value === "full",
                                                    className: _get(errors, `${checkboxFieldName}[${entryIndex}].value`, false) ? classes.fieldError : null,
                                                    ...props
                                                })}
                                                <ErrorMessage
                                                    name={`${checkboxFieldName}[${entryIndex}].value`}
                                                    render={msg => <div className={classes.error}>{msg}</div>}
                                                />
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        entryIndex === -1
                                            ? <span className={classes.default}>Non applicable</span>
                                            : <PackagingNumberPreview
                                                value={field[entryIndex].value}
                                                unit={checkboxField[entryIndex].value}
                                            />
                                    )
                                }
                            </TableCell>
                        )
                    })
                }
                {
                    !isBrandField && _getColumns(values).map((_, index) =>
                        <TableCell key={index}>
                            <span className={classes.default}>Non applicable</span>
                        </TableCell>
                    )
                }
            </TableRow>
        )
    }

    const _renderHeatingRow = (values, type, label, errors, props = {}) =>
        <TableRow>
            <TableCell className={classes.leftHeader}>{label}</TableCell>
            <TableCell></TableCell>
            {
                _getColumns(values).map((column, index) => {
                    const entryIndex = values.heatingInstructions.findIndex(el => _matchesColumn(el, column))

                    return (
                        <TableCell key={index}>
                            {
                                HEATING_FIELDS.map(({ key, fieldType, icon }, fieldIndex) =>
                                    <div key={fieldIndex} className={classes.iconFieldWrapper}>
                                        {_renderField(fieldType, {
                                            name: `heatingInstructions[${entryIndex}].value["${type}"]["${key}"]`,
                                            disabled: !editing,
                                            icon: icon,
                                            secondType: null !== icon ? FIELD_TYPES.NUMBER : null,
                                            className: _get(errors, `heatingInstructions[${entryIndex}].value["${type}"]["${key}"]`, false) ? classes.fieldError : null,
                                            ...props[key]
                                        })}
                                        <ErrorMessage
                                            name={`heatingInstructions[${entryIndex}].value["${type}"]["${key}"]`}
                                            render={msg => <div className={classes.error}>{msg}</div>}
                                        />
                                    </div>
                                )
                            }
                        </TableCell>
                    )
                })
            }
        </TableRow>

    const _renderAppImage = (publicId, onDelete, defaultShown = false) =>
        <div className={classes.imageContainer}>
            <CloudinaryImage
                imageId={publicId}
                containerClassName={defaultShown ? classes.defaultImage : classes.image}
                width={200}
                height={134}
            />
            {
                editing && !defaultShown &&
                <div className={classes.imageOverlay}>
                    {
                        onDelete &&
                        <div className={classes.deleteImageIcon}>
                            <DeleteIcon onClick={onDelete} />
                        </div>
                    }
                </div>
            }
        </div>

    const _renderKitchenImage = (publicId, onDelete) =>
        <div className={classes.imageContainer}>
            <CloudinaryImage
                imageId={publicId}
                containerClassName={classes.image}
                width={200}
                height={134}
            />
            {
                editing &&
                <div className={classes.imageOverlay}>
                    <div className={classes.deleteImageIcon}>
                        <DeleteIcon onClick={onDelete} />
                    </div>
                </div>
            }
        </div>

    const _renderAppImageRow = (values, setFieldValue, label, errors) => {
        const defaultImage = values.defaultValues.appImage

        return (
            <TableRow>
                <TableCell className={classes.leftHeader}>{label}</TableCell>
                <TableCell>
                    {
                        editing
                            ?
                            defaultImage && defaultImage.publicId
                                ?
                                _renderAppImage(
                                    defaultImage.publicId,
                                    () => _deleteAppImage(values, null, setFieldValue)
                                )
                                :
                                <div className={_get(errors, "defaultValues.appImage", false) ? classes.fieldError : null}>
                                    <ErrorMessage
                                        name="defaultValues.appImage"
                                        render={msg => <div className={classes.error}>{msg}</div>}
                                    />
                                    <span
                                        className={classes.addPhoto}
                                        onClick={() => _uploadAppImage(values, setFieldValue, null)}
                                    >
                                        Ajouter une photo
                                    </span>
                                </div>
                            :
                            defaultImage && defaultImage.publicId
                                ?
                                _renderAppImage(defaultImage.publicId, null)
                                :
                                <span className={classes.default}>Aucune image</span>
                    }
                </TableCell>
                {
                    _getColumns(values).map((column, index) => {
                        const currentAppImage = (values.appImage && values.appImage.find(el => _matchesColumn(el, column))) || null

                        return (
                            <TableCell key={index}>
                                {
                                    editing
                                        ?
                                        <>
                                            {
                                                currentAppImage && currentAppImage.value && currentAppImage.value.publicId
                                                    ?
                                                    _renderAppImage(
                                                        currentAppImage.value.publicId,
                                                        () => _deleteAppImage(values, column, setFieldValue)
                                                    )
                                                    :
                                                    defaultImage && defaultImage.publicId &&
                                                    <>
                                                        {_renderAppImage(defaultImage.publicId, null, true)}
                                                        <span
                                                            className={classes.addPhoto}
                                                            onClick={() => _uploadAppImage(values, setFieldValue, column)}
                                                        >
                                                            Ajouter une photo
                                                        </span>
                                                    </>
                                            }
                                        </>
                                        :
                                        <>
                                            {
                                                currentAppImage && currentAppImage.value && currentAppImage.value.publicId
                                                    ?
                                                    _renderAppImage(currentAppImage.value.publicId, null)
                                                    :
                                                    defaultImage && defaultImage.publicId &&
                                                    _renderAppImage(defaultImage.publicId, null, true)
                                            }
                                        </>
                                }
                            </TableCell>
                        )
                    })
                }
            </TableRow>
        )
    }

    const _renderKitchenImageRow = (values, setFieldValue, label) => {
        return (
            <TableRow>
                <TableCell className={classes.leftHeader}>{label}</TableCell>
                <TableCell>
                </TableCell>
                {
                    _getColumns(values).map((column, index) => {
                        const currentKitchenImage = (values.kitchenImage && values.kitchenImage.find(el => _matchesColumn(el, column))) || null

                        return (
                            <TableCell key={index}>
                                {
                                    editing
                                        ?
                                        <>
                                            {
                                                currentKitchenImage && currentKitchenImage.value && currentKitchenImage.value.publicId
                                                    ?
                                                    _renderKitchenImage(
                                                        currentKitchenImage.value.publicId,
                                                        () => _deleteKitchenImage(values, column, setFieldValue)
                                                    )
                                                    :
                                                    <span className={classes.addPhoto} onClick={() => _uploadKitchenImage(values, setFieldValue, column)}>Ajouter une photo</span>
                                            }
                                        </>
                                        :
                                        <>
                                            {
                                                currentKitchenImage && currentKitchenImage.value && currentKitchenImage.value.publicId
                                                    ?
                                                    _renderKitchenImage(currentKitchenImage.value.publicId, null)
                                                    :
                                                    <span className={classes.default}>Aucune image</span>
                                            }
                                        </>
                                }
                            </TableCell>
                        )
                    })
                }
            </TableRow>
        )
    }

    const _renderSectionSeparator = (title, colSpan) => {
        return (
            <TableRow className={classes.sectionSeparator}>
                <TableCell className={classes.sectionTitle} colSpan={colSpan}>{title}</TableCell>
            </TableRow>
        )
    }
        

    let initialValues = null
    let brandPackagings = null
    let brandSubPackagings = null
    let brandReusablePackagings = null
    let brandReusableSubPackagings = null
    let difficultyChooser = null

    if (parseRecipe) {
        initialValues = recipeDetailsFormInitialValues(parseRecipe)
        difficultyChooser = _buildDifficultyChooser()
    }

    if (packagings) {
        brandPackagings = _buildPackagings(packagings, false, true, false)
        brandSubPackagings = _buildPackagings(packagings, true, true, false)
        brandReusablePackagings = _buildPackagings(packagings, false, true, true)
        brandReusableSubPackagings = _buildPackagings(packagings, true, true, true)
    }

    if (!initialValues) return null
    
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={schema}
            enableReinitialize
            onSubmit={_onSubmit}
        >
            {({ values, errors, submitForm, resetForm, setFieldValue }) => {
                bindSubmitAndResetForm(submitForm, resetForm)

                return (
                    <>
                        {
                            !editing && (
                                <div className={classes.header}>
                                    <p>{/* Here will be the last edition */}</p>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.editButton}
                                        endIcon={<EditIcon />}
                                        onClick={onEdit}
                                    >
                                        Editer la recette
                                    </Button>
                                </div>
                            )
                        }
                        <Form>
                            <TableContainer className={`${classes.container} ${editing ? classes.marginTop : ""}`}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Marque</TableCell>
                                            <TableCell className={classes["brandHeaderDefault"]}>
                                                <div>Par défaut</div>
                                            </TableCell>
                                            {
                                                _getColumns(values).map((column, index) => {
                                                    if (column.isEnterprise) {
                                                        const enterprise = enterprises.find(e => e.objectId === column.key)
                                                        const enterpriseHeaderStyle = enterprise && enterprise.color
                                                            ? { borderBottom: `3px solid ${enterprise.color}`, color: enterprise.color, fontSize: 16 }
                                                            : null

                                                        return (
                                                            <TableCell key={index} className={classes.brandHeader} style={enterpriseHeaderStyle}>
                                                                <div>
                                                                    {
                                                                        enterprise && enterprise.logo && enterprise.logo.publicId &&
                                                                        <CloudinaryImage
                                                                            imageId={enterprise.logo.publicId}
                                                                            width={21}
                                                                            height={21}
                                                                            resize="fit"
                                                                            containerClassName={classes.logo}
                                                                        />
                                                                    }
                                                                    {enterprise ? enterprise.name : column.key}
                                                                </div>
                                                            </TableCell>
                                                        )
                                                    }

                                                    const completeBrand = getBrandBy("name", column.key)

                                                    return (
                                                        <TableCell key={index} className={classes[`brandHeader${completeBrand.name}`]}>
                                                            <div>
                                                                {
                                                                    completeBrand.image &&
                                                                    <img src={completeBrand.image} alt={`${completeBrand.label} logo`} className={classes.logo} />
                                                                }
                                                                {completeBrand.label}
                                                            </div>
                                                        </TableCell>
                                                    )
                                                })
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {/* MARKETING */}
                                        {_renderSectionSeparator("Marketing", _getColumns(values).length + 2)}
                                        {_renderAppImageRow(values, setFieldValue, "Photo vue client", true, "app", errors)}
                                        {_renderGenericRow(values, "commercialNames", FIELD_TYPES.TEXT, "Nom commercial", errors, true, {
                                            placeholder: values.defaultValues.commercialName
                                        })}
                                        {_renderGenericRow(values, "price", FIELD_TYPES.NUMBER, "Prix de vente", errors, true, {
                                            placeholder: values.defaultValues.price
                                        })}
                                        {_renderGenericRow(values, "tva", FIELD_TYPES.SELECT, "TVA", errors, true, {
                                            placeholder: values.defaultValues.tva,
                                            options: _tvaChooser()
                                        })}
                                        {_renderGenericRow(values, "dlc", FIELD_TYPES.NUMBER, "Date limite de consommation (en jours)", errors, true, {
                                            placeholder: values.defaultValues.dlc
                                        })}
                                        {_renderGenericRow(values, "description", FIELD_TYPES.TEXTAREA, "Description", errors, true, {
                                            placeholder: values.defaultValues.description
                                        })}

                                        {/* DRESSING */}
                                        {_renderSectionSeparator("Dressage", _getColumns(values).length + 2)}
                                        {_renderKitchenImageRow(values, setFieldValue, "Photo barquette", false, "kitchen")}
                                        {_renderGenericRow(values, "instructions", FIELD_TYPES.TEXTAREA, "Instruction de dressage", errors, true, {
                                            placeholder: values.defaultValues.instructions
                                        })}
                                        {_renderPackagingNumberRow(values, errors)}

                                        {_renderGenericRow(values, "gesters", FIELD_TYPES.NUMBER, "Nombre de gestes", errors)}
                                        {_renderGenericRow(values, "portionPerPlate", FIELD_TYPES.NUMBER, "Portion / Plaque", errors)}
                                        {_renderGenericRow(values, "difficulty", FIELD_TYPES.SELECT, "Difficulté de production", errors, true, {
                                            options: difficultyChooser
                                        })}

                                        {/* PACKAGING */}
                                        {_renderSectionSeparator("Packaging", _getColumns(values).length + 2)}
                                        {_renderGenericRow(values, "packaging", FIELD_TYPES.SELECT, "Packaging", errors, false, {
                                            options: brandPackagings
                                        })}
                                        {_renderGenericRow(values, "subPackaging", FIELD_TYPES.MULTISELECTAUTOCOMPLETE, "Sous-Packaging", errors, false, {
                                            options: brandSubPackagings,
                                            placeholder: "" // We don't want to show "par défaut" here
                                        })}
                                        {_renderGenericRow(values, "reusablePackaging", FIELD_TYPES.SELECT, "Packaging réutilisable", errors, false, {
                                            options: brandReusablePackagings
                                        })}
                                        {_renderGenericRow(values, "reusableSubPackaging", FIELD_TYPES.MULTISELECTAUTOCOMPLETE, "Sous-Packaging réutilisable", errors, false, {
                                            options: brandReusableSubPackagings,
                                            placeholder: "" // We don't want to show "par défaut" here
                                        })}
                                        {/* HEATING */}
                                        {_renderSectionSeparator("Chauffage", _getColumns(values).length + 2)}
                                        {_renderGenericRow(values, "preparation", FIELD_TYPES.SELECT, "Préparation", errors, true, {
                                            options: PREPARATION_VALUES,
                                            onChange: (e) => _updatePreparation(values, e.target.value, values.type, setFieldValue)
                                        })}
                                        {_renderGenericRow(values, "specialInstruction", FIELD_TYPES.TEXT, "Instruction spécifique", errors)}
                                        {
                                            "1" !== values.preparation &&
                                            <>
                                                {_renderHeatingRow(values, "microwave", "Micro-ondes", errors, {
                                                    power: { placeholder: _get(values, "defaultValues.heatingInstructions.microwave.power") },
                                                    duration: { placeholder: _get(values, "defaultValues.heatingInstructions.microwave.duration") },
                                                    instructions: { placeholder: _get(values, "defaultValues.heatingInstructions.microwave.instructions") }
                                                })}
                                                {_renderHeatingRow(values, "oven", "Four", errors, {
                                                    power: { placeholder: _get(values, "defaultValues.heatingInstructions.oven.power") },
                                                    duration: { placeholder: _get(values, "defaultValues.heatingInstructions.oven.duration") },
                                                    instructions: { placeholder: _get(values, "defaultValues.heatingInstructions.oven.instructions") }
                                                })}
                                                {_renderHeatingRow(values, "pan", "Poele", errors, {
                                                    power: { placeholder: _get(values, "defaultValues.heatingInstructions.pan.power") },
                                                    duration: { placeholder: _get(values, "defaultValues.heatingInstructions.pan.duration") },
                                                    instructions: { placeholder: _get(values, "defaultValues.heatingInstructions.pan.instructions") }
                                                })}
                                            </>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Form>
                    </>
                )
            }}
        </Formik>
    )
}

export default withStyles(RecipeDetailsStyles)(Details)